import React, { useState } from 'react'
import { ModalBase } from '@/components/modals/ModalBase'
import { adminPaymentService, REFUND_REASONS } from '@/services/adminPaymentService'

interface Props {
  isOpen: boolean
  onClose: () => void
  paymentId: string
  onSuccess?: () => void
}

/**
 * RefundModal — M5 admin involuntary refund flow.
 * Two-step: reason+notes -> confirmation.
 */
export const RefundModal: React.FC<Props> = ({ isOpen, onClose, paymentId, onSuccess }) => {
  const [reason, setReason] = useState<string>(REFUND_REASONS[0].value)
  const [notes, setNotes] = useState<string>('')
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleContinue = () => {
    setError(null)
    if (notes.trim().length < 20) {
      setError('Catatan admin minimal 20 karakter')
      return
    }
    setConfirming(true)
  }

  const handleSubmit = async () => {
    setError(null)
    setSubmitting(true)
    try {
      await adminPaymentService.issueRefund(paymentId, { reason, notes })
      onSuccess?.()
      onClose()
    } catch (e) {
      setError((e as Error).message || 'Refund gagal')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalBase isOpen={isOpen} onClose={onClose}>
      <h2 className="text-xl font-semibold mb-2">Issue Refund</h2>

      {!confirming ? (
        <div data-testid="refund-step-form" className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium">Alasan refund</span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full mt-1 border rounded px-3 py-2"
              data-testid="refund-reason"
            >
              {REFUND_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium">Catatan admin (min. 20 karakter)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full mt-1 border rounded px-3 py-2"
              data-testid="refund-notes"
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
              Batal
            </button>
            <button
              type="button"
              onClick={handleContinue}
              className="px-4 py-2 bg-primary text-white rounded"
              data-testid="refund-continue"
            >
              Lanjut
            </button>
          </div>
        </div>
      ) : (
        <div data-testid="refund-step-confirm" className="space-y-3 text-sm">
          <p>
            Refund akan: membalik komisi, memanggil Pivot, mengubah status pembayaran,
            membatalkan enrollment, dan mengirim email ke pembeli. Lanjutkan?
          </p>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setConfirming(false)} className="px-4 py-2 border rounded">
              Kembali
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 bg-red-600 text-white rounded disabled:opacity-50"
              data-testid="refund-confirm"
            >
              {submitting ? 'Memproses...' : 'Refund Sekarang'}
            </button>
          </div>
        </div>
      )}
    </ModalBase>
  )
}

export default RefundModal
